// src/account-setup.schema.ts
import { z } from "zod";
var IndustryEnum = z.enum([
  "SOLICITORS",
  "ESTATE_AGENTS",
  "FINANCIAL_INSTITUTIONS",
  "ACCOUNTANCY",
  "TCSPS",
  "HIGH_VALUE_DEALERS",
  "GAMBLING_OPERATOR",
  "ART_MARKET",
  "CRYPTOASSET_EXCHANGE",
  "CUSTODIAN_WALLET"
]);
var AccountSetupSchema = z.object({
  // User Information
  title: z.string().min(1, "Title is required").max(20),
  forename: z.string().min(1, "Forename is required").max(100),
  middleName: z.string().max(100).optional(),
  surname: z.string().min(1, "Surname is required").max(100),
  // Company Information
  industry: IndustryEnum,
  companyName: z.string().min(1, "Company name is required").max(200),
  companyWebsite: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  // Address
  addressLine1: z.string().min(1, "Address line 1 is required").max(200),
  addressLine2: z.string().max(200).optional(),
  townCity: z.string().min(1, "Town/City is required").max(100),
  county: z.string().max(100).optional(),
  postcode: z.string().min(1, "Postcode is required").max(20),
  country: z.string().min(1, "Country is required").max(2),
  // Terms
  termsAccepted: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms and conditions"
  })
});

// src/auth.schema.ts
import { z as z2 } from "zod";
var LoginSchema = z2.object({
  email: z2.string().email("Invalid email address").describe("User email address"),
  password: z2.string().min(1, "Password is required").describe("User password")
});
var RegisterSchema = z2.object({
  email: z2.string().email("Invalid email address").describe("User email address"),
  password: z2.string().min(8, "Password must be at least 8 characters long").describe("User password (minimum 8 characters)")
});

// src/check-depth.schema.ts
import { z as z3 } from "zod";
var CheckDepthTierSchema = z3.enum(["basic", "standard", "enhanced"]);

// src/client-details.schema.ts
import { z as z4 } from "zod";
var ClientTypeSchema = z4.enum(["NEW", "EXISTING"]);
var EntityTypeSchema = z4.enum(["INDIVIDUAL", "ORGANISATION"]);
var ClientTitleSchema = z4.enum([
  // Civil
  "MR",
  "MRS",
  "MISS",
  "MS",
  "MX",
  // Professional/Academic
  "DR",
  "PROF",
  // Religious
  "REV",
  "FR",
  "SR",
  "BR",
  "BISHOP",
  "ARCHBISHOP",
  "CARDINAL",
  "CANON",
  "IMAM",
  "SHEIKH",
  "RABBI",
  "PASTOR",
  // Honour/Peerage/Political
  "SIR",
  "DAME",
  "LORD",
  "LADY",
  "BARON",
  "BARONESS",
  "THE_HON",
  "THE_RT_HON",
  // Judicial
  "JUDGE",
  "JUSTICE",
  "MAGISTRATE",
  // Military
  "CAPT",
  "CMDR",
  "COL",
  "MAJ",
  "LT",
  "SGT",
  "CPL",
  "WO",
  "ADM",
  "AIR_CDRE",
  "GP_CAPT",
  "FLT_LT",
  // Royal
  "HM",
  "HRH",
  // Formal
  "ESQ"
]);
var ClientTitleLabels = {
  MR: "Mr",
  MRS: "Mrs",
  MISS: "Miss",
  MS: "Ms",
  MX: "Mx",
  DR: "Dr",
  PROF: "Prof",
  REV: "Rev",
  FR: "Fr",
  SR: "Sr",
  BR: "Br",
  BISHOP: "Bishop",
  ARCHBISHOP: "Archbishop",
  CARDINAL: "Cardinal",
  CANON: "Canon",
  IMAM: "Imam",
  SHEIKH: "Sheikh",
  RABBI: "Rabbi",
  PASTOR: "Pastor",
  SIR: "Sir",
  DAME: "Dame",
  LORD: "Lord",
  LADY: "Lady",
  BARON: "Baron",
  BARONESS: "Baroness",
  THE_HON: "The Hon",
  THE_RT_HON: "The Rt Hon",
  JUDGE: "Judge",
  JUSTICE: "Justice",
  MAGISTRATE: "Magistrate",
  CAPT: "Capt",
  CMDR: "Cmdr",
  COL: "Col",
  MAJ: "Maj",
  LT: "Lt",
  SGT: "Sgt",
  CPL: "Cpl",
  WO: "WO",
  ADM: "Adm",
  AIR_CDRE: "Air Cdre",
  GP_CAPT: "Gp Capt",
  FLT_LT: "Flt Lt",
  HM: "HM",
  HRH: "HRH",
  ESQ: "Esq"
};
var MatterTypeSchema = z4.enum([
  "CONVEYANCING_RESIDENTIAL",
  "CONVEYANCING_COMMERCIAL",
  "PROBATE_AND_WILLS",
  "FAMILY_LAW",
  "LITIGATION",
  "CORPORATE_AND_COMMERCIAL",
  "IMMIGRATION",
  "EMPLOYMENT",
  "PERSONAL_INJURY",
  "CRIMINAL_DEFENCE",
  "OTHER"
]);
var MatterTypeLabels = {
  CONVEYANCING_RESIDENTIAL: "Conveyancing (Residential)",
  CONVEYANCING_COMMERCIAL: "Conveyancing (Commercial)",
  PROBATE_AND_WILLS: "Probate and Wills",
  FAMILY_LAW: "Family Law",
  LITIGATION: "Litigation",
  CORPORATE_AND_COMMERCIAL: "Corporate and Commercial",
  IMMIGRATION: "Immigration",
  EMPLOYMENT: "Employment",
  PERSONAL_INJURY: "Personal Injury",
  CRIMINAL_DEFENCE: "Criminal Defence",
  OTHER: "Other"
};
var ClientCompanySchema = z4.object({
  id: z4.string().uuid().optional(),
  // Optional for new companies
  companyName: z4.string().min(1, "Company name is required"),
  dateOfIncorporation: z4.coerce.date().nullable().optional(),
  addressLine1: z4.string().optional(),
  addressLine2: z4.string().optional(),
  townCity: z4.string().optional(),
  county: z4.string().optional(),
  postcode: z4.string().optional(),
  country: z4.string().default("GB")
});
var DraftClientCompanySchema = z4.object({
  id: z4.string().uuid().optional(),
  companyName: z4.string().optional(),
  // No min length for drafts
  dateOfIncorporation: z4.coerce.date().nullable().optional(),
  addressLine1: z4.string().optional(),
  addressLine2: z4.string().optional(),
  townCity: z4.string().optional(),
  county: z4.string().optional(),
  postcode: z4.string().optional(),
  country: z4.string().optional()
});
var CreateClientDetailsSchema = z4.object({
  // Client Reference & Type
  clientRef: z4.string().min(1, "Client reference is required"),
  clientType: ClientTypeSchema.default("NEW"),
  entityType: EntityTypeSchema.optional().nullable().or(z4.literal("").transform(() => null)),
  // Handle empty string from form select
  matterType: MatterTypeSchema.optional().nullable().or(z4.literal("").transform(() => null)),
  // Handle empty string from form select
  // Legal matter flag (stored on RiskAssessment, controls SRA form visibility)
  isLegalMatter: z4.boolean().default(false),
  // Personal Information
  titles: z4.array(ClientTitleSchema).max(10, "Maximum of 10 titles allowed").optional().nullable(),
  forename: z4.string().min(1, "Forename is required"),
  middleName: z4.string().optional().nullable(),
  surname: z4.string().min(1, "Surname is required"),
  dateOfBirth: z4.coerce.date().refine((date) => date < /* @__PURE__ */ new Date(), {
    message: "Date of birth must be in the past"
  }),
  nationality: z4.string().min(2, "Nationality is required"),
  countryOfResidence: z4.string().min(2, "Country of residence is required"),
  // Address
  addressLine1: z4.string().optional().nullable(),
  addressLine2: z4.string().optional().nullable(),
  townCity: z4.string().min(1, "Town/City is required").optional().nullable(),
  county: z4.string().optional().nullable(),
  postcode: z4.string().optional().nullable(),
  // Occupation
  occupation: z4.string().min(1, "Occupation/Source of income is required"),
  // Companies (optional)
  companies: z4.array(ClientCompanySchema).optional(),
  // Consent (required for submission)
  consentGiven: z4.boolean().refine((val) => val === true, {
    message: "You must confirm consent to proceed"
  })
});
var SaveDraftClientDetailsSchema = z4.object({
  clientRef: z4.string().optional(),
  clientType: ClientTypeSchema.optional(),
  entityType: EntityTypeSchema.optional().nullable().or(z4.literal("").transform(() => null)),
  matterType: MatterTypeSchema.optional().nullable().or(z4.literal("").transform(() => null)),
  isLegalMatter: z4.boolean().optional(),
  titles: z4.array(ClientTitleSchema).max(10).optional().nullable(),
  forename: z4.string().optional(),
  middleName: z4.string().optional().nullable(),
  surname: z4.string().optional(),
  dateOfBirth: z4.coerce.date().optional().nullable(),
  nationality: z4.string().optional(),
  countryOfResidence: z4.string().optional(),
  addressLine1: z4.string().optional().nullable(),
  addressLine2: z4.string().optional().nullable(),
  townCity: z4.string().optional().nullable(),
  county: z4.string().optional().nullable(),
  postcode: z4.string().optional().nullable(),
  occupation: z4.string().optional(),
  companies: z4.array(DraftClientCompanySchema).optional(),
  consentGiven: z4.boolean().optional()
});
var ClientDetailsResponseSchema = z4.object({
  id: z4.string().uuid(),
  clientRef: z4.string(),
  clientType: ClientTypeSchema,
  entityType: EntityTypeSchema.nullable(),
  matterType: MatterTypeSchema.nullable(),
  titles: z4.array(ClientTitleSchema).nullable(),
  forename: z4.string(),
  middleName: z4.string().nullable(),
  surname: z4.string(),
  dateOfBirth: z4.coerce.date(),
  nationality: z4.string(),
  countryOfResidence: z4.string(),
  addressLine1: z4.string().nullable(),
  addressLine2: z4.string().nullable(),
  townCity: z4.string(),
  county: z4.string().nullable(),
  postcode: z4.string().nullable(),
  occupation: z4.string(),
  consentGiven: z4.boolean(),
  consentTimestamp: z4.coerce.date().nullable(),
  riskAssessmentId: z4.string().uuid(),
  companies: z4.array(
    ClientCompanySchema.extend({
      id: z4.string().uuid(),
      clientDetailsId: z4.string().uuid(),
      createdAt: z4.coerce.date(),
      updatedAt: z4.coerce.date()
    })
  ).optional(),
  createdAt: z4.coerce.date(),
  updatedAt: z4.coerce.date()
});
var StartAssessmentResponseSchema = z4.object({
  id: z4.string().uuid(),
  optimalKycRef: z4.string(),
  status: z4.literal("IN_PROGRESS"),
  createdAt: z4.coerce.date()
});

// src/email-verification.schema.ts
import { z as z5 } from "zod";
var VerifyEmailSchema = z5.object({
  token: z5.string().min(1, "Verification token is required").describe("Email verification token")
});

// src/env.schema.ts
import { z as z6 } from "zod";
var EnvironmentSchema = z6.enum(["local", "development", "staging", "production", "test"]);
var WorkerModeSchema = z6.enum(["api", "worker", "both"]);
var WorkerTypeSchema = z6.enum(["browser", "compute", "all"]);
var EnvSchema = z6.object({
  NODE_ENV: EnvironmentSchema.default("local").describe("Application environment"),
  PORT: z6.string().regex(/^\d+$/, "PORT must be a number").transform((val) => parseInt(val, 10)).pipe(z6.number().int().min(1).max(65535)).default("3000").describe("Server port (1-65535)"),
  DATABASE_URL: z6.string().min(1, "DATABASE_URL is required").describe("PostgreSQL connection string"),
  JWT_ACCESS_SECRET: z6.string().min(1, "JWT_ACCESS_SECRET is required").describe("Secret for access tokens"),
  JWT_REFRESH_SECRET: z6.string().min(1, "JWT_REFRESH_SECRET is required").describe("Secret for refresh tokens"),
  COOKIE_SECRET: z6.string().min(1, "COOKIE_SECRET is required").describe("Secret for cookie signing"),
  RATE_LIMIT_MAX: z6.string().regex(/^\d+$/, "RATE_LIMIT_MAX must be a number").transform((val) => parseInt(val, 10)).pipe(z6.number().int().min(1)).default("100").describe("Maximum requests per rate limit window"),
  OTEL_EXPORTER_OTLP_ENDPOINT: z6.string().optional().describe("OpenTelemetry collector endpoint (optional)"),
  PROM_AUTH_USER: z6.string().optional().describe("Prometheus basic auth username (optional)"),
  PROM_AUTH_PASS: z6.string().optional().describe("Prometheus basic auth password (optional)"),
  // Mailtrap SMTP Configuration
  SMTP_HOST: z6.string().default("sandbox.smtp.mailtrap.io").describe("SMTP server host"),
  SMTP_PORT: z6.string().regex(/^\d+$/, "SMTP_PORT must be a number").transform((val) => parseInt(val, 10)).pipe(z6.number().int().min(1).max(65535)).default("2525").describe("SMTP server port"),
  SMTP_USER: z6.string().optional().describe("SMTP username (Mailtrap credentials)"),
  SMTP_PASS: z6.string().optional().describe("SMTP password (Mailtrap credentials)"),
  SMTP_FROM_EMAIL: z6.string().email().default("noreply@okyc.ai").describe("From email address"),
  SMTP_FROM_NAME: z6.string().default("Optimal KYC").describe("From name"),
  // Frontend URL for password reset links
  FRONTEND_URL: z6.string().url().default("http://localhost:5173").describe("Frontend application URL"),
  // Evidence Storage Configuration
  STORAGE_TYPE: z6.enum(["local", "s3"]).default("local").describe("Storage provider type: local (filesystem) or s3"),
  EVIDENCE_STORAGE_PATH: z6.string().optional().describe("Local storage path for evidence files (only used when STORAGE_TYPE=local)"),
  S3_EVIDENCE_BUCKET: z6.string().optional().describe("S3 bucket name for evidence storage (required when STORAGE_TYPE=s3)"),
  S3_EVIDENCE_PREFIX: z6.string().default("evidence").describe("S3 key prefix for evidence files"),
  AWS_REGION: z6.string().default("eu-west-2").describe("AWS region for S3 bucket"),
  AWS_BEDROCK_MODEL_ID: z6.string().default("openai.gpt-oss-120b-1:0").refine(
    (val) => !val.toLowerCase().includes("anthropic") && !val.toLowerCase().includes("claude"),
    { message: "Anthropic/Claude models are not allowed. Use openai.gpt-oss-120b-1:0" }
  ).describe("AWS Bedrock model ID (must be gpt-oss-120b)"),
  // Agent API Keys
  COMPANIES_HOUSE_API_KEY: z6.string().optional().describe("Companies House API key for officer/company lookups"),
  SEARCH_PROVIDER: z6.enum(["brave", "google"]).optional().default("brave").describe("Search provider for adverse media and PEP screening (brave or google)"),
  BRAVE_SEARCH_API_KEY: z6.string().optional().describe("Brave Search API key for adverse media and PEP screening"),
  GOOGLE_API_KEY: z6.string().optional().describe("Google Custom Search API key"),
  GOOGLE_SEARCH_ENGINE_ID: z6.string().optional().describe("Google Custom Search Engine ID (cx parameter)"),
  BRAVE_GOGGLES_ENABLED: z6.string().optional().default("true").describe("Enable Brave Search Goggles to boost trusted media sources (true/false)"),
  BRAVE_GOGGLES_URL: z6.string().optional().describe(
    "URL to hosted Goggles file (e.g. https://your-domain.com/api/goggles/media-sources). If not set, rules are sent inline."
  ),
  // Queue Configuration
  REDIS_URL: z6.string().optional().describe("Redis connection URL for job queue (e.g., redis://localhost:6379)"),
  WORKER_MODE: WorkerModeSchema.default("api").describe(
    "Instance mode: api (HTTP only), worker (queue only), both (legacy combined mode)"
  ),
  WORKER_TYPE: WorkerTypeSchema.default("all").describe(
    "Queue type to process: browser (Chromium jobs), compute (AI jobs), all (development)"
  ),
  DISABLE_NODE_WORKER: z6.enum(["true", "false"]).default("false").transform((val) => val === "true").describe("Disable in-process queue consumer (rely on ECS workers)"),
  // Parity Runner Configuration
  PARITY_RUNNER_ENABLED: z6.enum(["true", "false"]).default("false").transform((val) => val === "true").describe("Enable parity runner evaluation system (admin only)"),
  // Public Registration
  REGISTRATION_ENABLED: z6.enum(["true", "false"]).default("false").transform((val) => val === "true").describe(
    "Enable public user registration (POST /auth/register). Defaults to false to prevent unauthorised account creation on live environments."
  ),
  // GDPR/Support Contact Configuration
  GDPR_CONTACT_EMAIL: z6.string().email().default("dpo@okyc.ai").describe("Data Protection Officer contact email"),
  SUPPORT_EMAIL: z6.string().email().default("support@okyc.ai").describe("Support team contact email"),
  // Stripe Payment Configuration
  STRIPE_SECRET_KEY: z6.string().optional().describe("Stripe secret API key (sk_test_... or sk_live_...)"),
  STRIPE_WEBHOOK_SECRET: z6.string().optional().describe("Stripe webhook signing secret (whsec_...)"),
  STRIPE_PUBLISHABLE_KEY: z6.string().optional().describe("Stripe publishable key for frontend (pk_test_... or pk_live_...)"),
  // CORS Configuration
  CORS_ALLOWED_ORIGINS: z6.string().optional().describe(
    "Comma-separated list of allowed CORS origins (e.g., https://app.okyc.ai,https://staging.okyc.ai). If not set, CORS is disabled."
  ),
  // Document Upload Configuration
  UPLOAD_TOKEN_EXPIRY_DAYS: z6.string().regex(/^\d+$/, "UPLOAD_TOKEN_EXPIRY_DAYS must be a number").transform((val) => parseInt(val, 10)).pipe(z6.number().int().min(1).max(30)).optional().describe("Upload token expiry in days (default: 7, max: 30)"),
  UPLOAD_DOCUMENT_EXPIRY_DAYS: z6.string().regex(/^\d+$/, "UPLOAD_DOCUMENT_EXPIRY_DAYS must be a number").transform((val) => parseInt(val, 10)).pipe(z6.number().int().min(1).max(90)).optional().describe("Document retention period in days after upload (default: 10, max: 90)"),
  // Risk Scoring Configuration
  MAX_RISK_SCORE: z6.string().regex(/^\d+$/, "MAX_RISK_SCORE must be a number").transform((val) => parseInt(val, 10)).pipe(z6.number().int().min(0).max(100)).optional().describe("Maximum risk score cap (0-100). Defaults to 99.")
}).refine((data) => data.STORAGE_TYPE !== "s3" || !!data.S3_EVIDENCE_BUCKET, {
  message: "S3_EVIDENCE_BUCKET is required when STORAGE_TYPE is s3"
}).refine(
  (data) => {
    if (data.WORKER_MODE !== "api" && !data.REDIS_URL) {
      return false;
    }
    return true;
  },
  {
    message: "REDIS_URL is required when WORKER_MODE is worker or both"
  }
).refine(
  (data) => {
    if (data.STRIPE_SECRET_KEY && !data.STRIPE_WEBHOOK_SECRET) {
      return false;
    }
    return true;
  },
  {
    message: "STRIPE_WEBHOOK_SECRET is required when STRIPE_SECRET_KEY is set"
  }
).refine(
  (data) => {
    if (data.NODE_ENV !== "production") return true;
    try {
      const { hostname } = new URL(data.FRONTEND_URL);
      return hostname !== "localhost" && hostname !== "127.0.0.1";
    } catch {
      return false;
    }
  },
  {
    message: "FRONTEND_URL must be set to a non-localhost URL in production (used in report-ready email links)"
  }
);

// src/feature-flag.schema.ts
import { z as z7 } from "zod";
var JsonValueSchema = z7.lazy(
  () => z7.union([
    z7.string(),
    z7.number(),
    z7.boolean(),
    z7.record(JsonValueSchema),
    z7.array(JsonValueSchema)
  ])
);
var CreateFeatureFlagSchema = z7.object({
  key: z7.string().min(1, "Key is required").regex(/^[a-z0-9_]+$/, "Key must contain only lowercase letters, numbers, and underscores").describe("Unique feature flag key (snake_case)"),
  enabled: z7.boolean().default(false).describe("Whether the feature flag is enabled"),
  description: z7.string().optional().describe("Human-readable description of the feature flag"),
  strategies: JsonValueSchema.optional().describe("Targeting strategies (JSON object)")
});
var UpdateFeatureFlagSchema = z7.object({
  enabled: z7.boolean().optional().describe("Whether the feature flag is enabled"),
  description: z7.string().optional().describe("Human-readable description of the feature flag"),
  strategies: JsonValueSchema.optional().describe("Targeting strategies (JSON object)")
});
var FeatureFlagSchema = z7.object({
  key: z7.string(),
  enabled: z7.boolean(),
  description: z7.string().nullable(),
  strategies: z7.union([JsonValueSchema, z7.null()]),
  createdAt: z7.date(),
  updatedAt: z7.date()
});

// src/password-reset.schema.ts
import { z as z8 } from "zod";
var ForgotPasswordSchema = z8.object({
  email: z8.string().email("Invalid email address").describe("User email address")
});
var ResetPasswordSchema = z8.object({
  token: z8.string().min(1, "Reset token is required").describe("Password reset token"),
  password: z8.string().min(8, "Password must be at least 8 characters").describe("New password"),
  confirmPassword: z8.string().min(1, "Please confirm your password").describe("Password confirmation")
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});

// src/payment.schema.ts
import { z as z9 } from "zod";
var MIN_PAYMENT_AMOUNT_PENCE = 500;
var MAX_PAYMENT_AMOUNT_PENCE = 1e6;
var CreatePaymentIntentSchema = z9.object({
  amount: z9.number().int("Amount must be a whole number (pence)").min(MIN_PAYMENT_AMOUNT_PENCE, `Minimum amount is ${MIN_PAYMENT_AMOUNT_PENCE} pence (\xA35.00)`).max(
    MAX_PAYMENT_AMOUNT_PENCE,
    `Maximum amount is ${MAX_PAYMENT_AMOUNT_PENCE} pence (\xA310,000.00)`
  ).describe("Amount in pence (e.g., 500 = \xA35.00)")
});
var PaymentIntentResponseSchema = z9.object({
  clientSecret: z9.string().describe("Stripe client secret for confirming payment"),
  paymentIntentId: z9.string().describe("Stripe PaymentIntent ID"),
  amount: z9.number().describe("Amount in pence")
});
var PaymentStatusSchema = z9.enum([
  "PENDING",
  "PROCESSING",
  "SUCCEEDED",
  "FAILED",
  "CANCELLED"
]);

// src/project.schema.ts
import { z as z10 } from "zod";
var ProjectSchema = z10.object({
  id: z10.string().uuid().describe("Project unique identifier"),
  name: z10.string().min(3, "Name must be at least 3 characters").describe("Project name"),
  description: z10.string().nullable().optional().describe("Project description (optional)"),
  userId: z10.string().uuid().describe("Owner user ID"),
  createdAt: z10.date().describe("Creation timestamp"),
  updatedAt: z10.date().describe("Last update timestamp")
});
var CreateProjectSchema = z10.object({
  name: z10.string().min(1, "Project name is required").max(100, "Project name must not exceed 100 characters").describe("The name of the project"),
  description: z10.string().max(500, "Description must not exceed 500 characters").optional().describe("A brief description of the project")
});
var UpdateProjectSchema = z10.object({
  name: z10.string().min(1, "Project name is required").max(100, "Project name must not exceed 100 characters").optional().describe("The name of the project"),
  description: z10.string().max(500, "Description must not exceed 500 characters").optional().describe("A brief description of the project")
});

// src/risk-assessment.schema.ts
import { z as z11 } from "zod";
var RiskLevelSchema = z11.enum(["LOW", "MEDIUM", "HIGH"]);
var DocumentRequestSchema = z11.object({
  id: z11.string(),
  documentType: z11.string(),
  otherDescription: z11.string().optional(),
  dateRequested: z11.coerce.date(),
  received: z11.boolean(),
  downloadUrl: z11.string().optional()
});
var AssessmentStatusSchema = z11.enum(["IN_PROGRESS", "COMPLETED", "DELETED"]);
var ScreeningJobStatusSchema = z11.enum([
  "NOT_STARTED",
  // No screening has been initiated
  "FAST_CHECKS_DONE",
  // Quick checks complete, adverse media queued
  "PROCESSING",
  // Adverse media check in progress
  "COMPLETED",
  // All checks complete
  "FAILED"
  // Processing failed after retries
]);
var ScreeningCheckIdSchema = z11.enum([
  "UK",
  // HM Treasury Sanctions List
  "US",
  // US Consolidated Sanctions List (OFAC)
  "UN",
  // UN Sanctions List
  "EU",
  // EU Sanctions List
  "PEP",
  // Politically Exposed Persons
  "ADVERSE_MEDIA",
  // Adverse Media screening
  "COMPANIES_HOUSE",
  // Companies House officer search
  "CPI",
  // Corruption Perceptions Index
  "FATF"
  // Financial Action Task Force jurisdictions
]);
var ALL_SCREENING_CHECKS = [
  "UK",
  "US",
  "UN",
  "EU",
  "PEP",
  "ADVERSE_MEDIA",
  "COMPANIES_HOUSE",
  "CPI",
  "FATF"
];
var REQUIRED_CHECKS = ["UK"];
var PaymentFlowStepSchema = z11.enum(["REQUEST_DOCS", "MATCH_REPORT", "EDD"]);
var RiskAssessmentSchema = z11.object({
  id: z11.string(),
  optimalKycRef: z11.string(),
  // e.g., "OK10"
  clientRef: z11.string(),
  // e.g., "SRA013"
  clientName: z11.string(),
  clientEmail: z11.string().email().optional(),
  riskLevel: RiskLevelSchema,
  status: AssessmentStatusSchema,
  screeningJobStatus: ScreeningJobStatusSchema.optional(),
  // Queue processing status
  paymentFlowStep: PaymentFlowStepSchema.nullable().optional(),
  // Current step in payment flow for resumption
  isLegalMatter: z11.boolean().default(false),
  // Whether this is a legal matter (for SRA form generation)
  documentRequestLinkGenerated: z11.boolean(),
  documentRequests: z11.array(DocumentRequestSchema).optional(),
  hasNewDocuments: z11.boolean().default(false),
  reportDownloadWindowDays: z11.number().nullable(),
  // null = expired
  reportExpired: z11.boolean().default(false),
  needsRefresh: z11.boolean().default(false),
  // Whether assessment needs refresh based on user's due diligence frequency
  reportData: z11.record(z11.unknown()).nullable().optional(),
  // FinalResult JSON from screening
  formattedReport: z11.string().nullable().optional(),
  // Human-readable screening report
  enabledChecks: z11.array(ScreeningCheckIdSchema).optional(),
  // Which checks were run
  dateReportGenerated: z11.coerce.date().nullable(),
  dateDeleted: z11.coerce.date().nullable(),
  createdAt: z11.coerce.date(),
  updatedAt: z11.coerce.date()
});
var RiskAssessmentListResponseSchema = z11.object({
  data: z11.array(RiskAssessmentSchema),
  total: z11.number(),
  page: z11.number(),
  pageSize: z11.number()
});
var RiskAssessmentSortColumnSchema = z11.enum([
  "clientRef",
  "riskLevel",
  "dateReportGenerated",
  "createdAt"
]);
var RiskAssessmentFiltersSchema = z11.object({
  search: z11.string().optional(),
  riskLevels: z11.union([RiskLevelSchema, z11.array(RiskLevelSchema)]).optional().transform((val) => val ? Array.isArray(val) ? val : [val] : void 0),
  reportDownloadWindow: z11.coerce.number().optional(),
  dateFrom: z11.coerce.date().optional(),
  dateTo: z11.coerce.date().optional(),
  status: AssessmentStatusSchema.optional(),
  page: z11.coerce.number().default(1),
  pageSize: z11.coerce.number().default(20),
  sortBy: RiskAssessmentSortColumnSchema.optional(),
  sortOrder: z11.enum(["asc", "desc"]).optional()
});
var GenerateDocumentLinkSchema = z11.object({
  assessmentId: z11.string(),
  clientEmail: z11.string().email(),
  documentTypes: z11.array(z11.string()).min(1),
  otherDescription: z11.string().optional()
});
var DocumentTypeSchema = z11.enum([
  "IDENTITY_DOCUMENT",
  "PROOF_OF_ADDRESS",
  "PROOF_OF_INCOME",
  "BANK_STATEMENT",
  "TAX_RETURN",
  "OTHER"
]);
var DocumentTypeLabels = {
  IDENTITY_DOCUMENT: "Identity Document",
  PROOF_OF_ADDRESS: "Proof of Address",
  PROOF_OF_INCOME: "Proof of Income",
  BANK_STATEMENT: "Bank Statement",
  TAX_RETURN: "Tax Return",
  OTHER: "Other"
};
var CreateRiskAssessmentSchema = z11.object({
  clientRef: z11.string().min(1),
  clientName: z11.string().min(1),
  clientEmail: z11.string().email().optional(),
  riskLevel: RiskLevelSchema,
  reportData: z11.record(z11.unknown()).optional(),
  // FinalResult JSON
  formattedReport: z11.string().optional()
});
var UpdateRiskAssessmentSchema = z11.object({
  clientRef: z11.string().min(1).optional(),
  clientName: z11.string().min(1).optional(),
  clientEmail: z11.string().email().optional(),
  riskLevel: RiskLevelSchema.optional(),
  status: AssessmentStatusSchema.optional()
});
var BulkOperationSchema = z11.object({
  ids: z11.array(z11.string().uuid()).min(1)
});

// src/service-keys.ts
import { z as z12 } from "zod";
var SERVICE_KEYS = [
  "pep_sanctions_screening",
  "ai_confidence_report",
  "match_risk_report",
  "documentation_request"
];
var ServiceKeyEnum = z12.enum(SERVICE_KEYS);
var TIER_KEYS = ["tier_basic", "tier_standard", "tier_enhanced"];
var TierKeyEnum = z12.enum(TIER_KEYS);
var BILLABLE_KEYS = [...SERVICE_KEYS, ...TIER_KEYS];
var BillableKeyEnum = z12.enum(BILLABLE_KEYS);
var PAID_SERVICE_PROPERTIES = [
  "initialScreening",
  "matchReport",
  "eddReport",
  "docRequest"
];
var SERVICE_KEY_PAID_PROPERTY = {
  pep_sanctions_screening: "initialScreening",
  ai_confidence_report: "eddReport",
  match_risk_report: "matchReport",
  documentation_request: "docRequest"
};
var TIER_INCLUDED_SERVICES = {
  tier_basic: ["pep_sanctions_screening"],
  tier_standard: ["pep_sanctions_screening", "match_risk_report"],
  tier_enhanced: ["pep_sanctions_screening", "match_risk_report", "ai_confidence_report"]
};

// src/settings.schema.ts
import { z as z13 } from "zod";
var DueDiligenceFrequencyEnum = z13.enum(["MONTHLY", "QUARTERLY", "ANNUALLY", "NEVER"]);
var UpdateProfileSchema = z13.object({
  title: z13.string().min(1, "Title is required").max(20).optional(),
  forename: z13.string().min(1, "Forename is required").max(100).optional(),
  middleName: z13.string().max(100).nullable().optional(),
  surname: z13.string().min(1, "Surname is required").max(100).optional()
});
var UpdateCompanySchema = z13.object({
  name: z13.string().min(1, "Company name is required").max(200).optional(),
  industry: IndustryEnum.optional(),
  website: z13.string().url("Please enter a valid URL").nullable().optional(),
  addressLine1: z13.string().min(1, "Address line 1 is required").max(200).optional(),
  addressLine2: z13.string().max(200).nullable().optional(),
  townCity: z13.string().min(1, "Town/City is required").max(100).optional(),
  county: z13.string().max(100).nullable().optional(),
  postcode: z13.string().min(1, "Postcode is required").max(20).optional(),
  country: z13.string().min(2, "Country is required").max(2).optional()
});
var UpdateDueDiligenceSchema = z13.object({
  frequency: DueDiligenceFrequencyEnum
});
var DeleteAccountSchema = z13.object({
  confirmEmail: z13.string().email("Please enter your email"),
  password: z13.string().min(1, "Password is required")
});
var SettingsFormSchema = z13.object({
  // User Info
  title: z13.string().min(1, "Title is required"),
  forename: z13.string().min(1, "Forename is required"),
  middleName: z13.string().optional(),
  surname: z13.string().min(1, "Surname is required"),
  // Due Diligence
  dueDiligenceAlertFrequency: DueDiligenceFrequencyEnum,
  // Company
  companyName: z13.string().min(1, "Company name is required"),
  industry: IndustryEnum,
  companyWebsite: z13.string().url("Please enter a valid URL").optional().or(z13.literal("")),
  // Address
  addressLine1: z13.string().min(1, "Address line 1 is required"),
  addressLine2: z13.string().optional(),
  townCity: z13.string().min(1, "Town/City is required"),
  county: z13.string().optional(),
  postcode: z13.string().min(1, "Postcode is required"),
  country: z13.string().min(2).max(2)
});
var PricingItemSchema = z13.object({
  key: z13.string(),
  name: z13.string(),
  description: z13.string().nullable(),
  priceGBP: z13.string()
  // Decimal as string for precision
});
var ProfileResponseSchema = z13.object({
  user: z13.object({
    id: z13.string(),
    email: z13.string(),
    title: z13.string().nullable(),
    forename: z13.string().nullable(),
    middleName: z13.string().nullable(),
    surname: z13.string().nullable(),
    dueDiligenceAlertFrequency: DueDiligenceFrequencyEnum,
    balance: z13.string()
    // Decimal as string
  }),
  company: z13.object({
    id: z13.string(),
    name: z13.string(),
    industry: IndustryEnum,
    website: z13.string().nullable(),
    addressLine1: z13.string().nullable(),
    addressLine2: z13.string().nullable(),
    townCity: z13.string().nullable(),
    county: z13.string().nullable(),
    postcode: z13.string().nullable(),
    country: z13.string()
  }).nullable()
});

// src/user.schema.ts
import { z as z14 } from "zod";
var RoleSchema = z14.object({
  id: z14.string().uuid(),
  name: z14.string().min(1),
  description: z14.string().nullable(),
  createdAt: z14.date(),
  updatedAt: z14.date()
});
var CompanySchema = z14.object({
  id: z14.string().uuid(),
  name: z14.string(),
  industry: IndustryEnum,
  website: z14.string().nullable(),
  addressLine1: z14.string().nullable(),
  addressLine2: z14.string().nullable(),
  townCity: z14.string().nullable(),
  county: z14.string().nullable(),
  postcode: z14.string().nullable(),
  country: z14.string(),
  createdAt: z14.date(),
  updatedAt: z14.date()
});
var PermissionSchema = z14.object({
  id: z14.string().uuid(),
  name: z14.string().min(1),
  description: z14.string().nullable(),
  createdAt: z14.date(),
  updatedAt: z14.date()
});
var UserSchema = z14.object({
  id: z14.string().uuid(),
  email: z14.string().email(),
  passwordHash: z14.string(),
  roleId: z14.string().uuid(),
  role: RoleSchema.optional(),
  failedLoginAttempts: z14.number().int().min(0),
  lockedUntil: z14.date().nullable(),
  // Personal Information (from account setup)
  title: z14.string().nullable(),
  forename: z14.string().nullable(),
  middleName: z14.string().nullable(),
  surname: z14.string().nullable(),
  accountSetupComplete: z14.boolean(),
  // Email verification
  emailVerified: z14.boolean(),
  emailVerifiedAt: z14.date().nullable(),
  // Company relationship
  companyId: z14.string().uuid().nullable(),
  createdAt: z14.date(),
  updatedAt: z14.date()
});
var PublicUserSchema = UserSchema.omit({
  passwordHash: true,
  failedLoginAttempts: true,
  lockedUntil: true
}).extend({
  role: z14.object({
    id: z14.string().uuid(),
    name: z14.string(),
    description: z14.string().nullable()
  }).optional(),
  company: CompanySchema.nullable().optional(),
  balance: z14.union([z14.string(), z14.number()]).transform((val) => Number(val))
});
var CreateUserSchema = z14.object({
  email: z14.string().email("Invalid email address").describe("User email address"),
  password: z14.string().min(8, "Password must be at least 8 characters").describe("User password")
});

// src/utils.ts
import { z as z15 } from "zod";
function createUpdateSchema(schema) {
  const shape = schema.shape;
  const optionalShape = Object.keys(shape).reduce(
    (acc, key) => {
      acc[key] = shape[key].optional();
      return acc;
    },
    {}
  );
  return z15.object(optionalShape);
}
function omitSensitive(schema, keys) {
  const omitKeys = keys.reduce(
    (acc, key) => {
      acc[key] = true;
      return acc;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {}
  );
  return schema.omit(omitKeys);
}
export {
  ALL_SCREENING_CHECKS,
  AccountSetupSchema,
  AssessmentStatusSchema,
  BILLABLE_KEYS,
  BillableKeyEnum,
  BulkOperationSchema,
  CheckDepthTierSchema,
  ClientCompanySchema,
  ClientDetailsResponseSchema,
  ClientTitleLabels,
  ClientTitleSchema,
  ClientTypeSchema,
  CompanySchema,
  CreateClientDetailsSchema,
  CreateFeatureFlagSchema,
  CreatePaymentIntentSchema,
  CreateProjectSchema,
  CreateRiskAssessmentSchema,
  CreateUserSchema,
  DeleteAccountSchema,
  DocumentRequestSchema,
  DocumentTypeLabels,
  DocumentTypeSchema,
  DraftClientCompanySchema,
  DueDiligenceFrequencyEnum,
  EntityTypeSchema,
  EnvSchema,
  EnvironmentSchema,
  FeatureFlagSchema,
  ForgotPasswordSchema,
  GenerateDocumentLinkSchema,
  IndustryEnum,
  LoginSchema,
  MAX_PAYMENT_AMOUNT_PENCE,
  MIN_PAYMENT_AMOUNT_PENCE,
  MatterTypeLabels,
  MatterTypeSchema,
  PAID_SERVICE_PROPERTIES,
  PaymentFlowStepSchema,
  PaymentIntentResponseSchema,
  PaymentStatusSchema,
  PermissionSchema,
  PricingItemSchema,
  ProfileResponseSchema,
  ProjectSchema,
  PublicUserSchema,
  REQUIRED_CHECKS,
  RegisterSchema,
  ResetPasswordSchema,
  RiskAssessmentFiltersSchema,
  RiskAssessmentListResponseSchema,
  RiskAssessmentSchema,
  RiskAssessmentSortColumnSchema,
  RiskLevelSchema,
  RoleSchema,
  SERVICE_KEYS,
  SERVICE_KEY_PAID_PROPERTY,
  SaveDraftClientDetailsSchema,
  ScreeningCheckIdSchema,
  ScreeningJobStatusSchema,
  ServiceKeyEnum,
  SettingsFormSchema,
  StartAssessmentResponseSchema,
  TIER_INCLUDED_SERVICES,
  TIER_KEYS,
  TierKeyEnum,
  UpdateCompanySchema,
  UpdateDueDiligenceSchema,
  UpdateFeatureFlagSchema,
  UpdateProfileSchema,
  UpdateProjectSchema,
  UpdateRiskAssessmentSchema,
  UserSchema,
  VerifyEmailSchema,
  WorkerModeSchema,
  WorkerTypeSchema,
  createUpdateSchema,
  omitSensitive
};
//# sourceMappingURL=index.js.map